import React, { useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { Kbd } from '../../ui/kbd';
import { cn } from '../../../lib/utils';

// Toolbar pieces shared by the full-bleed admin pages (Catalogue, Blog, Live
// chat): a slim title bar, outline tool buttons, the green primary action,
// a "/"-focused search box and a segmented switch.

/**
 * 56px page bar: title and meta on the left, tools on the right. On phones the tools
 * (search, filters) drop to a second row that scrolls sideways, and actions stay by the title.
 */
export const PageBar: React.FC<{ title: React.ReactNode; meta?: React.ReactNode; children?: React.ReactNode; actions?: React.ReactNode }> = ({
  title,
  meta,
  children,
  actions,
}) => (
  <header className="flex min-h-14 shrink-0 flex-wrap items-center gap-3 border-b border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] px-4 max-[700px]:gap-y-2.5 max-[700px]:py-2.5">
    <div className="flex min-w-0 items-baseline gap-3 max-[700px]:flex-1 max-[700px]:basis-0">
      <h1 className="shrink-0 font-montserrat text-[17px] font-extrabold tracking-[-0.01em] text-[color:var(--oa-text)]">{title}</h1>
      {meta ? <span className="truncate text-[12px] tabular-nums text-[color:var(--oa-muted)]">{meta}</span> : null}
    </div>
    {children ? (
      <div className="contents max-[700px]:order-3 max-[700px]:-mx-4 max-[700px]:flex max-[700px]:basis-[calc(100%+2rem)] max-[700px]:items-center max-[700px]:gap-2 max-[700px]:overflow-x-auto max-[700px]:px-4 max-[700px]:[scrollbar-width:none] max-[700px]:[&>*]:shrink-0">
        {children}
      </div>
    ) : null}
    {actions ? <div className="ml-auto flex shrink-0 items-center gap-1.5">{actions}</div> : null}
  </header>
);

export const ToolButton = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }>(
  ({ active, children, className, ...rest }, ref) => (
    <button
      ref={ref}
      type="button"
      {...rest}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-[12.5px] font-medium transition-colors disabled:opacity-60',
        active ? 'border-[color:var(--oa-text)] bg-[color:var(--oa-pill-bg)] text-[color:var(--oa-pill-text)]' : 'border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] text-[color:var(--oa-text)] hover:border-[color:var(--oa-line-3)] hover:bg-[color:var(--oa-raised)]',
        className
      )}
    >
      {children}
    </button>
  )
);
ToolButton.displayName = 'ToolButton';

export const PrimaryButton = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(({ children, className, ...rest }, ref) => (
  <button
    ref={ref}
    type="button"
    {...rest}
    className={cn(
      'inline-flex h-8 items-center gap-1.5 rounded-md bg-[color:var(--oa-accent)] px-3 text-[12.5px] font-semibold text-[color:var(--oa-on-accent)] transition-colors hover:bg-[color:var(--oa-accent-2)] disabled:opacity-60',
      className
    )}
  >
    {children}
  </button>
));
PrimaryButton.displayName = 'PrimaryButton';

/** Search box; "/" focuses it from anywhere on the page, Esc clears it. */
export const SearchInput: React.FC<{ value: string; onChange: (v: string) => void; placeholder?: string; className?: string }> = ({
  value,
  onChange,
  placeholder = 'Search',
  className,
}) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (e.key === '/' && !typing) {
        e.preventDefault();
        ref.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className={cn('relative w-[320px] max-w-[40vw] max-[700px]:w-[200px] max-[700px]:max-w-none', className)}>
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--oa-muted)]" />
      <input
        ref={ref}
        type="search"
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            onChange('');
            (e.target as HTMLInputElement).blur();
          }
        }}
        placeholder={placeholder}
        className="h-8 w-full rounded-md border border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)] pl-8 pr-8 text-[13px] text-[color:var(--oa-text)] placeholder:text-[color:var(--oa-faint)] outline-none transition-colors focus:border-[color:var(--oa-accent)] focus:bg-[color:var(--oa-card)] focus:ring-2 focus:ring-[color:color-mix(in_oklab,var(--oa-accent)_20%,transparent)]"
      />
      <div className="absolute right-1.5 top-1/2 -translate-y-1/2">
        {value ? (
          <button type="button" onClick={() => onChange('')} className="rounded p-0.5 text-[color:var(--oa-muted)] hover:bg-[color:var(--oa-raised-2)] hover:text-[color:var(--oa-text)]" aria-label="Clear search">
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <Kbd className="bg-[color:var(--oa-raised-2)] text-[color:var(--oa-muted)]">/</Kbd>
        )}
      </div>
    </div>
  );
};

/** Pill switch for a handful of mutually exclusive views. */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  className,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: React.ReactNode; count?: number }[];
  className?: string;
}) {
  return (
    <div role="tablist" className={cn('inline-flex h-8 items-center gap-0.5 rounded-md border border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)] p-0.5', className)}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            className={cn(
              'inline-flex h-full items-center gap-1.5 rounded px-2.5 text-[12.5px] font-medium transition-colors',
              active ? 'bg-[color:var(--oa-card)] text-[color:var(--oa-text)] shadow-[0_1px_2px_rgba(11,15,25,0.08)]' : 'text-[color:var(--oa-muted)] hover:text-[color:var(--oa-text)]'
            )}
          >
            {o.label}
            {o.count !== undefined ? <span className={cn('tabular-nums text-[11px]', active ? 'text-[color:var(--oa-muted)]' : 'text-[color:var(--oa-faint)]')}>{o.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

/** Centred empty or loading message inside a panel. */
export const EmptyState: React.FC<{ icon?: React.ComponentType<{ className?: string }>; title: React.ReactNode; hint?: React.ReactNode; children?: React.ReactNode }> = ({
  icon: Icon,
  title,
  hint,
  children,
}) => (
  <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
    {Icon ? <Icon className="mb-3 h-8 w-8 text-[color:var(--oa-faint)]" /> : null}
    <p className="text-[13.5px] font-medium text-[color:var(--oa-text)]">{title}</p>
    {hint ? <p className="mt-1 text-[12.5px] text-[color:var(--oa-muted)]">{hint}</p> : null}
    {children ? <div className="mt-4">{children}</div> : null}
  </div>
);
